import { triniumApi } from '@api/Trinium/slice';
import {
	FetchVesselRequest,
	FetchVoyageRequest,
	FetchVoyagesRequest,
	sortVesselResponse,
	transformVoyageResponse,
	Vessel,
	Voyage,
	VoyageRaw
} from './types';
import { updateVoyageCache } from '@/Vessel/types';
import { updateConsignmentVoyageCache } from '../Consignment/actions';

export const vesselSlice = triniumApi.injectEndpoints({
	endpoints(builder) {
		return {
			fetchVessels: builder.query<Vessel[], FetchVesselRequest>({
				query: ({ startsWith }) => {
					return {
						url: `/vessels`,
						params: {
							startsWith
						}
					};
				},
				transformResponse: (response: Vessel[]) => {
					return sortVesselResponse(response);
				}
			}),

			fetchVoyages: builder.query<Voyage[], FetchVoyagesRequest>({
				query: ({ vesselId, catTransType }) => ({
					// "N/A" is a possible vesselId and needs to be escaped manually
					url: `/vessels/${vesselId.replace('/', '\\/')}/voyages`,
					params: catTransType ? { catTransType } : undefined
				}),
				transformResponse: (response: VoyageRaw[]) => response.map(transformVoyageResponse),
				providesTags: (_result, _error, { vesselId, catTransType }) => [
					catTransType?.toLowerCase() === 'e' ? { type: 'export-voyages', id: vesselId } : { type: 'import-voyages', id: vesselId }
				]
			}),

			fetchVoyage: builder.query<Voyage, FetchVoyageRequest>({
				query: ({ vesselId, voyageId }) => ({
					// "N/A" is a possible vesselId and needs to be escaped manually
					url: `/vessels/${vesselId.replace('/', '\\/')}/voyages/${voyageId}`
				}),
				transformResponse: transformVoyageResponse
			}),

			addVessel: builder.mutation<Vessel, Partial<Vessel>>({
				query: (vessel) => ({
					url: `/vessels`,
					method: 'POST',
					body: vessel
				})
			}),

			addVoyage: builder.mutation<Voyage, { vesselId: string; voyage: Partial<VoyageRaw> }>({
				query: ({ vesselId, voyage }) => ({
					url: `/vessels/${vesselId.replace('/', '\\/')}/voyages`,
					method: 'POST',
					body: voyage
				}),
				transformResponse: transformVoyageResponse,
				async onQueryStarted({ vesselId }, api) {
					const { data: newVoyage } = await api.queryFulfilled;
					updateVoyageCache(newVoyage, vesselId);
				}
			}),

			updateVoyage: builder.mutation<
				Voyage,
				{
					vesselId: string;
					voyage: Pick<VoyageRaw, 'id'> & Partial<VoyageRaw>;
				}
			>({
				query: ({ vesselId, voyage }) => ({
					url: `/vessels/${vesselId.replace('/', '\\/')}/voyages/${String(voyage.id)}`,
					method: 'PATCH',
					body: voyage
				}),
				transformResponse: transformVoyageResponse,
				async onQueryStarted({ vesselId, voyage }, api) {
					const { data: response } = await api.queryFulfilled;
					updateVoyageCache(response, vesselId);
					// Keep the embedded voyage snapshot on any consignment referencing it (e.g. the Consignment
					// Details header) in sync with the edited FRD/Cutoff without requiring a refetch.
					updateConsignmentVoyageCache(response);
					// The Vessel & Voyage modal seeds itself from the single-voyage endpoint. Without this, the
					// next open of the same voyage prefills from the pre-edit snapshot until its refetch lands —
					// and saving in that window would write the stale dates straight back.
					void api.dispatch(
						vesselSlice.util.upsertQueryData('fetchVoyage', { vesselId, voyageId: String(voyage.id) }, response)
					);
				}
			})
		};
	}
});

export const {
	useFetchVesselsQuery,
	useLazyFetchVesselsQuery,
	useFetchVoyagesQuery,
	useFetchVoyageQuery,
	useAddVesselMutation,
	useAddVoyageMutation,
	useUpdateVoyageMutation
} = vesselSlice;
